import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { experienceService } from '../../services/experienceService';
import { propertyService } from '../../services/propertyService';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent } from '../../components/ui/Card';
import { Building2 } from 'lucide-react';
import LocationMapPicker from '../../components/common/LocationMapPicker';
import { AddressAutocompleteField } from '../../components/common/AddressAutocompleteField';
import { useAuthStore } from '../../stores/authStore';
import type { AxiosError } from 'axios';

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Array<{ field: string; message: string }>;
  };
  message?: string;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormState {
  // Step 1 — Basics
  title: string;
  description: string;
  duration: string;
  groupSizeMin: number;
  groupSizeMax: string;
  // Step 2 — Location
  addressLine1: string;
  city: string;
  state: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  // Step 3 — Pricing & Tags
  pricePerGuest: string;
  currency: string;
  tagIds: string[];
}

const INITIAL_FORM: FormState = {
  title: '', description: '', duration: '', groupSizeMin: 1, groupSizeMax: '',
  addressLine1: '', city: '', state: '', country: '', latitude: null, longitude: null,
  pricePerGuest: '', currency: 'INR', tagIds: [],
};

const CURRENCIES = [
  { label: 'INR (₹)', value: 'INR' },
  { label: 'USD ($)', value: 'USD' },
  { label: 'EUR (€)', value: 'EUR' },
  { label: 'GBP (£)', value: 'GBP' },
];

const TOTAL_STEPS = 3;

// ─── Step progress bar ────────────────────────────────────────────────────────

function StepBar({ current }: { current: number }) {
  const labels = ['Basics', 'Location', 'Pricing & Photos'];
  return (
    <div className="mt-4 flex items-center gap-0">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                done ? 'bg-primary-500 text-white' :
                active ? 'bg-primary-600 text-white ring-4 ring-primary-100' :
                'bg-gray-100 text-gray-400'
              }`}>
                {done ? '✓' : step}
              </div>
              <span className={`mt-1 hidden text-xs sm:block ${active ? 'font-semibold text-primary-600' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
            {i < labels.length - 1 && (
              <div className={`mb-4 h-0.5 flex-1 transition-colors ${step < current ? 'bg-primary-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TagPill({ label, selected, onToggle }: { label: string; selected: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-all ${
        selected ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300'
      }`}
    >
      {label}
    </button>
  );
}

/** Reusable red asterisk for required field labels */
function Req() {
  return <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>;
}

// ─── Field → step mapping (for auto-navigation on error) ─────────────────────

const FIELD_TO_STEP: Record<string, number> = {
  title: 1, description: 1, duration: 1, groupSizeMin: 1, groupSizeMax: 1,
  addressLine1: 2, city: 2, state: 2, country: 2, latitude: 2, longitude: 2,
  pricePerGuest: 3, currency: 3,
};

const FIELD_LABELS: Record<string, string> = {
  title: 'Experience title', description: 'Description', duration: 'Duration',
  groupSizeMin: 'Min group size', groupSizeMax: 'Max group size',
  addressLine1: 'Street address', city: 'City', state: 'State', country: 'Country',
  latitude: 'Latitude', longitude: 'Longitude',
  pricePerGuest: 'Price per guest', currency: 'Currency',
};

function parseApiError(err: AxiosError<ApiErrorBody>): { message: string; step?: number } {
  const apiError = err.response?.data?.error;

  if (!apiError) {
    return { message: err.message || 'Network error — please check your connection and try again.' };
  }

  if (apiError.code === 'VALIDATION_ERROR' && Array.isArray(apiError.details) && apiError.details.length > 0) {
    const lines = apiError.details.map((d: { field: string; message: string }) => {
      const label = FIELD_LABELS[d.field] ?? d.field;
      return `• ${label}: ${d.message}`;
    });
    const firstField = apiError.details[0]?.field;
    const step = FIELD_TO_STEP[firstField];
    return {
      message: 'Please fix the following before continuing:\n' + lines.join('\n'),
      step,
    };
  }

  const codeMessages: Record<string, string> = {
    UNAUTHORIZED: 'Your session has expired — please sign in again.',
    FORBIDDEN: 'You do not have permission to create experiences.',
    CONFLICT: apiError.message ?? 'An experience with this title already exists.',
    NOT_FOUND: 'The requested resource was not found. Please refresh and try again.',
    RATE_LIMITED: 'Too many requests — please wait a moment and try again.',
    INTERNAL_ERROR: 'A server error occurred. Please try again in a few minutes.',
  };
  const known = apiError.code ? codeMessages[apiError.code] : undefined;
  if (known) return { message: known };

  if (!apiError.code) {
    const status = err.response?.status ?? 0;
    if (status === 401) return { message: 'Your session has expired — please sign in again.' };
    if (status === 403) return { message: 'You do not have permission to perform this action.' };
    if (status === 413) return { message: 'One or more files are too large. Maximum allowed size is 10 MB.' };
    if (status >= 500) return { message: 'A server error occurred. Please try again in a few minutes.' };
  }

  return { message: apiError.message ?? 'Something went wrong. Please try again.' };
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CreateExperiencePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();

  // PLATFORM_STAFF / PLATFORM_ADMIN / SUPER_ADMIN creating on behalf of an org
  const role = user?.roles?.[0];
  const isPlatformStaff = role === 'PLATFORM_STAFF' || role === 'PLATFORM_ADMIN' || role === 'SUPER_ADMIN';
  const orgIdFromUrl = searchParams.get('organizationId') ?? '';
  const orgNameFromUrl = searchParams.get('orgName') ?? '';
  // Each fixed-tier role has its own Organizations detail route -- redirect back to whichever one
  // matches the current user so the post-create toast/back-nav lands somewhere they can access.
  const orgDetailBasePath =
    role === 'SUPER_ADMIN' ? '/dashboard/super-admin/organizations'
    : role === 'PLATFORM_ADMIN' ? '/dashboard/platform-admin/organizations'
    : '/dashboard/platform-staff/organizations';

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [photos, setPhotos] = useState<{ file: File; preview: string; uploading: boolean; error: boolean }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [photoErrors, setPhotoErrors] = useState<string[]>([]);
  const [showMapPicker, setShowMapPicker] = useState(false);

  const set = (field: keyof FormState) => (val: FormState[typeof field]) =>
    setForm((prev) => ({ ...prev, [field]: val }));

  const fieldChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      set(field)(e.target.value as never);

  // ── Data fetches ──────────────────────────────────────────────────────────

  const { data: tagGroups = {}, isLoading: loadingTags } = useQuery({
    queryKey: ['experience-tags'],
    queryFn: propertyService.getExperienceTags,
    staleTime: 10 * 60_000,
  });

  // ── Photo handling ────────────────────────────────────────────────────────

  const addPhotos = (files: File[]) => {
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      uploading: false,
      error: false,
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadPhotos = async (experienceId: string) => {
    const errs: string[] = [];
    for (let i = 0; i < photos.length; i++) {
      setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: true } : p));
      try {
        await experienceService.uploadPhoto(experienceId, photos[i].file);
        setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: false } : p));
      } catch (err) {
        setPhotos((prev) => prev.map((p, idx) => idx === i ? { ...p, uploading: false, error: true } : p));
        const name = photos[i].file.name;
        const status = (err as AxiosError).response?.status;
        if (status === 400) {
          errs.push(`"${name}": File type or size is not supported (JPEG, PNG, WebP up to 10 MB only).`);
        } else if (status === 413) {
          errs.push(`"${name}": File is too large. Maximum allowed size is 10 MB.`);
        } else {
          errs.push(`"${name}": Upload failed — please try again.`);
        }
      }
    }
    if (errs.length > 0) setPhotoErrors(errs);
  };

  // ── Final submit ──────────────────────────────────────────────────────────

  const handleFinalSubmit = async () => {
    if (!form.title.trim()) { setSubmitError('Experience title is required.'); setStep(1); return; }
    if (!form.duration.trim()) { setSubmitError('Duration is required.'); setStep(1); return; }
    if (!form.city.trim()) { setSubmitError('City is required.'); setStep(2); return; }
    if (!form.country.trim()) { setSubmitError('Country is required.'); setStep(2); return; }
    if (isPlatformStaff && !orgIdFromUrl) {
      setSubmitError('Please select an organization first. Go back and choose an organization from the Organizations page.');
      return;
    }
    setSubmitError('');
    setPhotoErrors([]);
    setIsSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        duration: form.duration.trim(),
        groupSizeMin: Number(form.groupSizeMin) || 1,
        groupSizeMax: form.groupSizeMax ? Number(form.groupSizeMax) : undefined,
        addressLine1: form.addressLine1.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim() || undefined,
        country: form.country.trim(),
        latitude: form.latitude ?? undefined,
        longitude: form.longitude ?? undefined,
        tagIds: form.tagIds.length > 0 ? form.tagIds : undefined,
        // Required when PLATFORM_STAFF/ADMIN/SUPER_ADMIN creates on behalf of an org
        ...(isPlatformStaff && orgIdFromUrl ? { organizationId: orgIdFromUrl } : {}),
      };

      // 1. Create the experience
      const res = await experienceService.create(payload);
      const experience = res.data.data;
      if (!experience?.id) throw new Error('Failed to create experience — no ID returned');
      const experienceId = experience.id;

      // 2. Set pricing
      if (form.pricePerGuest && Number(form.pricePerGuest) > 0) {
        await experienceService.updatePricing(experienceId, {
          pricePerGuest: Number(form.pricePerGuest),
          currency: form.currency,
        });
      }

      // 3. Upload photos
      if (photos.length > 0) {
        await uploadPhotos(experienceId);
      }

      if (isPlatformStaff && orgIdFromUrl) {
        navigate(
          `${orgDetailBasePath}/${orgIdFromUrl}?experienceCreated=1&experienceName=${encodeURIComponent(form.title)}`,
        );
      } else {
        // No experiences-list page exists yet for ORG_ADMIN/ORG_MANAGER (out of scope for this
        // pass -- only the creation flow was requested) -- redirect to their own dashboard instead
        // of a route that doesn't exist.
        navigate('/dashboard');
      }
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorBody>;
      console.error('[CreateExperience] API error:', axiosErr.response?.data ?? axiosErr.message);
      const { message, step: errorStep } = parseApiError(axiosErr);
      setSubmitError(message);
      if (errorStep && errorStep < TOTAL_STEPS) {
        setStep(errorStep);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step validation ───────────────────────────────────────────────────────

  const isStep1Valid = form.title.trim().length >= 2 && !!form.duration.trim();
  const isStep2Valid = !!form.city.trim() && !!form.country.trim();
  const isStep3Valid = !!form.pricePerGuest && Number(form.pricePerGuest) > 0 && !!form.currency;

  // ── Render ────────────────────────────────────────────────────────────────

  // Platform-tier role with no org selected → block the form and send them back
  if (isPlatformStaff && !orgIdFromUrl) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <Building2 size={40} className="mx-auto mb-4 text-gray-300" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">No organization selected</h2>
        <p className="text-sm text-gray-500 mb-6">
          You must choose an organization before adding an experience. Go to the Organizations page,
          find the organization, and click <strong>Add Experience</strong>.
        </p>
        <Button onClick={() => navigate(orgDetailBasePath)}>
          Go to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">List an experience</h1>
        <p className="mt-1 text-sm text-gray-500">Step {step} of {TOTAL_STEPS}</p>
        <StepBar current={step} />
      </div>

      {/* Org context banner (shown when a platform-tier role is creating on behalf of an org) */}
      {isPlatformStaff && orgIdFromUrl && (
        <div className="mb-5 flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <Building2 size={18} className="text-primary-600 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-primary-800">
              Adding experience on behalf of <span className="font-semibold">{orgNameFromUrl || 'selected organization'}</span>
            </p>
            <p className="text-xs text-primary-600 mt-0.5">
              This experience will be owned by the organization admin.{' '}
              <button
                type="button"
                onClick={() => navigate(orgDetailBasePath)}
                className="underline hover:no-underline"
              >
                Change organization
              </button>
            </p>
          </div>
        </div>
      )}

      {submitError && (
        <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {submitError.split('\n').map((line, i) => (
            <p key={i} className={i === 0 ? 'font-semibold' : 'mt-1 ml-2'}>{line}</p>
          ))}
        </div>
      )}

      {/* ── Step 1: Basics ─────────────────────────────────────────────────── */}
      {step === 1 && (
        <Card>
          <CardContent className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Tell us about the experience</h2>
              <p className="mt-1 text-sm text-gray-500">What is it, and how long does it take?</p>
            </div>

            <div className="space-y-4">
              <Input
                label="Experience title *"
                value={form.title}
                onChange={fieldChange('title')}
                placeholder="e.g. Sunset Kayaking, Heritage Walking Tour"
                required
              />
              <div className="space-y-1">
                <label htmlFor="experience-description" className="block text-sm font-medium text-gray-700">
                  Description <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="experience-description"
                  value={form.description}
                  onChange={fieldChange('description') as React.ChangeEventHandler<HTMLTextAreaElement>}
                  rows={3}
                  placeholder="Describe what guests will do…"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <Input
                label="Duration *"
                value={form.duration}
                onChange={fieldChange('duration')}
                placeholder="e.g. 2 hours, Half day, 3 days"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <span id="group-min-label" className="block text-sm font-semibold text-gray-700">Min group size</span>
                <div className="flex items-center gap-3" role="group" aria-labelledby="group-min-label">
                  <button type="button" onClick={() => set('groupSizeMin')(Math.max(1, form.groupSizeMin - 1))}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">−</button>
                  <span className="w-8 text-center text-base font-semibold">{form.groupSizeMin}</span>
                  <button type="button" onClick={() => set('groupSizeMin')(form.groupSizeMin + 1)}
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 text-lg text-gray-700 hover:bg-gray-50">+</button>
                </div>
              </div>
              <Input
                label="Max group size (optional)"
                type="number"
                min="1"
                value={form.groupSizeMax}
                onChange={fieldChange('groupSizeMax')}
                placeholder="No limit"
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button onClick={() => setStep(2)} disabled={!isStep1Valid}>Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Location ───────────────────────────────────────────────── */}
      {step === 2 && (
        <>
          <Card>
            <CardContent className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Where does it happen?</h2>
                <p className="mt-1 text-sm text-gray-500">Full address and GPS coordinates help guests find the meeting point.</p>
              </div>

              {/* Places-Autocomplete-first: pick a place and every field below auto-fills */}
              <div className="space-y-1.5">
                {/* Not a <label> -- AddressAutocompleteField's underlying input already carries
                    its own aria-label; a sibling <label> with no htmlFor would be an
                    unassociated duplicate accessible name, not a real fix. */}
                <p className="text-sm font-medium text-gray-700">Search for the address</p>
                <AddressAutocompleteField
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  onSelect={(addr) => setForm((prev) => ({
                    ...prev,
                    addressLine1: addr.addressLine1 || prev.addressLine1,
                    city: addr.city || prev.city,
                    state: addr.state || prev.state,
                    country: addr.country || prev.country,
                    latitude: addr.lat,
                    longitude: addr.lng,
                  }))}
                />
                <p className="text-xs text-gray-400">Or fill in the fields below manually.</p>
              </div>

              <Input label="Street address" value={form.addressLine1} onChange={fieldChange('addressLine1')} placeholder="Meeting point address (optional)" />
              <div className="grid grid-cols-2 gap-4">
                <Input label="City *" value={form.city} onChange={fieldChange('city')} required placeholder="Mumbai" />
                <Input label="State / Province" value={form.state} onChange={fieldChange('state')} placeholder="Maharashtra" />
              </div>
              <Input label="Country *" value={form.country} onChange={fieldChange('country')} required placeholder="India" />

              <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">GPS coordinates <span className="font-normal text-gray-400">(optional but recommended)</span></p>
                    <p className="text-xs text-gray-500 mt-0.5">Helps guests find the meeting point after booking</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="flex items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 transition-colors"
                  >
                    <span>🗺</span>
                    <span>{form.latitude != null ? 'Change on map' : 'Pick on map'}</span>
                  </button>
                </div>

                {form.latitude != null && form.longitude != null ? (
                  <div className="flex items-center gap-3 rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <span className="text-lg" aria-hidden="true">📍</span>
                    <div className="text-sm">
                      <span className="text-gray-600">Lat: </span>
                      <span className="font-mono font-medium text-gray-900">{form.latitude.toFixed(6)}</span>
                      <span className="mx-2 text-gray-300">|</span>
                      <span className="text-gray-600">Lng: </span>
                      <span className="font-mono font-medium text-gray-900">{form.longitude.toFixed(6)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { set('latitude')(null); set('longitude')(null); }}
                      aria-label="Clear GPS coordinates"
                      className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>No location pinned yet</span>
                  </div>
                )}

                <details>
                  <summary className="cursor-pointer text-xs text-primary-600 hover:text-primary-700">Enter coordinates manually</summary>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label htmlFor="experience-latitude" className="block text-xs font-medium text-gray-600">Latitude (−90 to 90)</label>
                      <input
                        id="experience-latitude"
                        type="number"
                        step="any"
                        min="-90"
                        max="90"
                        value={form.latitude ?? ''}
                        onChange={(e) => set('latitude')(e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="e.g. 19.0760"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="experience-longitude" className="block text-xs font-medium text-gray-600">Longitude (−180 to 180)</label>
                      <input
                        id="experience-longitude"
                        type="number"
                        step="any"
                        min="-180"
                        max="180"
                        value={form.longitude ?? ''}
                        onChange={(e) => set('longitude')(e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="e.g. 72.8777"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </details>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)}>← Back</Button>
                <Button onClick={() => setStep(3)} disabled={!isStep2Valid}>Next →</Button>
              </div>
            </CardContent>
          </Card>

          {showMapPicker && (
            <LocationMapPicker
              lat={form.latitude}
              lng={form.longitude}
              onConfirm={(lat, lng) => {
                set('latitude')(lat);
                set('longitude')(lng);
                setShowMapPicker(false);
              }}
              onClose={() => setShowMapPicker(false)}
            />
          )}
        </>
      )}

      {/* ── Step 3: Pricing & Photos ───────────────────────────────────────── */}
      {step === 3 && (
        <Card>
          <CardContent className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Pricing &amp; photos</h2>
              <p className="mt-1 text-sm text-gray-500">Set your per-guest rate and add photos.</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-1">
                <h3 className="text-sm font-semibold text-gray-700">Pricing</h3>
                <Req />
                <span className="text-xs text-gray-400 ml-1">(required before review)</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Price per guest *" type="number" min="0" value={form.pricePerGuest} onChange={fieldChange('pricePerGuest')} placeholder="0.00" required />
                <div className="space-y-1">
                  <label htmlFor="experience-currency" className="block text-sm font-medium text-gray-700">Currency<Req /></label>
                  <select id="experience-currency" value={form.currency} onChange={fieldChange('currency')}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500">
                    {CURRENCIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">
                  Photos
                  <span className="ml-1.5 text-xs font-normal text-red-500">(at least 1 photo required for review)</span>
                </h3>
                <p className="mt-1 text-xs text-gray-500">Great photos get more bookings. Upload at least one — you can add more after.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-video overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                    <img src={p.preview} alt="" className="h-full w-full object-cover" />
                    {p.uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      </div>
                    )}
                    {p.error && <div className="absolute bottom-0 left-0 right-0 bg-red-500 py-1 text-center text-xs text-white">Failed</div>}
                    {i === 0 && !p.uploading && (
                      <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">Cover</span>
                    )}
                    <button type="button" onClick={() => removePhoto(i)}
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80">×</button>
                  </div>
                ))}
                <label className="flex aspect-video cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 transition-colors hover:border-primary-400 hover:text-primary-500">
                  <span className="text-2xl">+</span>
                  <span className="text-xs font-medium">Add photo</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="sr-only"
                    onChange={(e) => e.target.files && addPhotos(Array.from(e.target.files))} />
                </label>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">
                What kind of experience is this? <span className="font-normal text-gray-400">(optional)</span>
              </h3>
              {loadingTags ? (
                <div className="flex flex-wrap gap-2">{[1,2,3,4,5,6].map(i => <div key={i} className="h-8 w-24 animate-pulse rounded-full bg-gray-100" />)}</div>
              ) : (
                Object.entries(tagGroups).map(([group, tags]) => (
                  <div key={group}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{group.replace('_', ' ')}</p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <TagPill key={tag.id} label={tag.name} selected={form.tagIds.includes(tag.id)}
                          onToggle={() => {
                            const ids = form.tagIds.includes(tag.id)
                              ? form.tagIds.filter((id) => id !== tag.id)
                              : [...form.tagIds, tag.id];
                            set('tagIds')(ids);
                          }} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            {photoErrors.length > 0 && (
              <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <p className="font-semibold">Some photos could not be uploaded:</p>
                {photoErrors.map((msg, i) => <p key={i} className="mt-1 ml-2">{msg}</p>)}
                <p className="mt-2 text-xs text-amber-600">Your experience was created — you can retry uploading photos later.</p>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
              <Button onClick={handleFinalSubmit} disabled={!isStep3Valid} loading={isSubmitting}>
                {isSubmitting ? 'Creating…' : 'Create experience'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
